import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBackRounded,
  AutoAwesomeRounded,
  DownloadRounded,
  UploadFileOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  documentCraftingErrorMessage,
  downloadDocumentCraftingRevisionFile,
  getDocumentCraftingError,
} from "../../../Api/documentCrafting";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingFormField,
  DocumentCraftingIngestConfidence,
  DocumentCraftingInvalidFormError,
  DocumentCraftingRevisePayload,
  DocumentCraftingRevisionDetail,
  DocumentCraftingRevisionOrigin,
  DocumentCraftingTypeDefinition,
  DocumentCraftingUpload,
} from "../../../Models/documentCrafting";
import {
  useCreateDocumentCraftingRevision,
  useDocumentCraftingDocument,
  useDocumentCraftingModels,
  useDocumentCraftingRevision,
  useDocumentCraftingTypes,
} from "../../../Query/useDocumentCrafting";
import ConfirmDialog from "../Common/ConfirmDialog";
import {
  ErrorState,
  InfoRow,
  LoadingState,
  PageHeader,
  Panel,
} from "../Common/DocumentCraftingUi";
import ModelSelect, { resolveDocumentCraftingModelId } from "../Common/ModelSelect";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import {
  downloadFileName,
  formatDate,
  formatDateTime,
  useUserName,
} from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import FormSummary from "../Create/FormSummary";
import SchemaForm, { focusFormField, useFormErrorText } from "../Create/SchemaForm";
import {
  FieldStep,
  FormErrors,
  FormValues,
  MAX_FILES_PER_RUN,
  PendingFile,
  PendingFiles,
  buildInitialValues,
  collectFileFields,
  countPendingFiles,
  fieldsOfStep,
  altsForRequest,
  filesForRequest,
  groupUploadsByPath,
  initialUploadSelection,
  fileFieldPath,
  isKnownPath,
  mapServerErrors,
  normalizeErrorPath,
  selectedUploadIds,
  setAt,
  trimValues,
  typeRuleErrors,
  validateFields,
  validateText,
  withUploadSelection,
  withoutErrors,
} from "../Create/documentFormModel";
// The detail page's upload dialog and its `?revision=draft` parameter.
import UploadRevisionDialog from "../Detail/UploadRevisionDialog";
import { DRAFT_PARAM } from "../Detail/detailModel";
import {
  CHANGE_REQUEST_MAX_LENGTH,
  SMALL_CHANGE_REQUEST_LIMIT,
  changeRequestHistory,
  countNewlyUnticked,
  editableInRevision,
  isSmallChange,
  structuredFieldsChanged,
  untickedUploadIds,
} from "./reviseModel";

const CHANGE_REQUEST_KEY = "change_request";
const MODEL_KEY = "model_id";
const TOTAL_FILES_KEY = "_files";
const OWN_KEYS = [CHANGE_REQUEST_KEY, MODEL_KEY, TOTAL_FILES_KEY, "base_revision_id"];
const FORM_STEPS: FieldStep[] = ["basics", "details", "files"];

type DialogKey = "smallChange" | "draftExists" | "upload";

interface Initial {
  values: FormValues;
  uploadsByPath: Record<string, DocumentCraftingUpload[]>;
  /** Earlier uploads no field fits; they stay in use. */
  otherUploadIds: number[];
  /** Uploads ticked at the start (those the base revision used). */
  selectedIds: number[];
}

/** Form values and upload selection of the base revision. */
const initialFromBase = (
  fields: DocumentCraftingFormField[],
  base: DocumentCraftingRevisionDetail,
): Initial => {
  const fileFields = collectFileFields(editableInRevision(fields));
  const selection = initialUploadSelection(
    fileFields,
    base.uploads ?? [],
    base.inputs?.upload_ids ?? null,
  );
  const values = withUploadSelection(
    buildInitialValues(fields, null, base.inputs?.values ?? {}),
    selection.byPath,
  );
  return {
    values,
    uploadsByPath: groupUploadsByPath(base.uploads ?? [], fileFields),
    otherUploadIds: selection.other,
    selectedIds: selectedUploadIds(values, fileFields),
  };
};

/** Briefing and earlier change requests of the base revision, read-only. */
const HistoryPanel: React.FC<{
  document: DocumentCraftingDocumentDetail;
  base: DocumentCraftingRevisionDetail;
  readOnlyFields: DocumentCraftingFormField[];
  values: FormValues;
}> = ({ document, base, readOnlyFields, values }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const history = changeRequestHistory(document.revisions, base.number);
  const duplicateNote = base.inputs?.duplicate_note?.trim();

  return (
    <Panel title={t("create.revise.history.title")} subtitle={t("create.revise.history.subtitle")}>
      <InfoRow label={t("create.wizard.language")} value={labels.language(document.language)} />
      <FormSummary fields={readOnlyFields} values={values} files={{}} />
      {duplicateNote && (
        <InfoRow label={t("create.duplicate.noteLabel")} value={duplicateNote} />
      )}
      <Typography sx={{ mt: 2, mb: 1, fontSize: 13, fontWeight: 700, color: color.gray900 }}>
        {t("create.revise.history.changeRequests")}
      </Typography>
      {history.length ? (
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.25 }}>
          {history.map((entry) => (
            <Box
              component="li"
              key={entry.revisionId}
              sx={{ pl: 1.5, borderLeft: `3px solid ${color.gray200}` }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: color.gray500 }}>
                {[
                  t("common.revisions.revision", { number: entry.number }),
                  entry.revision.origin === DocumentCraftingRevisionOrigin.MANUAL_UPLOAD
                    ? labels.origin(entry.revision.origin)
                    : null,
                  formatDate(entry.revision.saved_at ?? entry.revision.created_at, i18n.language),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
              {entry.changeRequest && (
                <Typography sx={{ fontSize: 13, color: color.gray700, whiteSpace: "pre-line" }}>
                  {entry.changeRequest}
                </Typography>
              )}
              {entry.refinements.map((text, index) => (
                <Typography
                  key={`${index}-${text}`}
                  sx={{ fontSize: 13, color: color.gray600, whiteSpace: "pre-line", mt: 0.5 }}
                >
                  {t("create.revise.history.refinement", { text })}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: color.gray500 }}>
          {t("create.revise.history.none")}
        </Typography>
      )}
    </Panel>
  );
};

interface FormProps {
  document: DocumentCraftingDocumentDetail;
  definition: DocumentCraftingTypeDefinition;
  base: DocumentCraftingRevisionDetail;
  initial: Initial;
  focusField: string | null;
  workspaceMaxMb: number | null;
  onBaseChange: (revisionId: number) => void;
}

const ReviseForm: React.FC<FormProps> = ({
  document,
  definition,
  base,
  initial,
  focusField,
  workspaceMaxMb,
  onBaseChange,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const errorText = useFormErrorText();
  const models = useDocumentCraftingModels();
  const createRevision = useCreateDocumentCraftingRevision();

  const [values, setValues] = useState<FormValues>(initial.values);
  const [files, setFiles] = useState<PendingFiles>({});
  const [altUpdates, setAltUpdates] = useState<Record<number, string>>({});
  const [changeRequest, setChangeRequest] = useState("");
  const [modelId, setModelId] = useState<number | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKey | null>(null);
  const [downloading, setDownloading] = useState(false);

  const fields: DocumentCraftingFormField[] = definition.fields;
  const editable = editableInRevision(fields);
  const readOnlyFields = fields.filter((field) => !editable.includes(field));
  const fileFields = collectFileFields(editable);
  const nativeExt = base.artefacts?.native_ext || definition.native_ext || "";
  // SPEC §4.6: the hint is shown for every type with an editable native file.
  const handEditable =
    definition.supports_manual_upload && ["docx", "pptx"].includes(nativeExt.toLowerCase());
  const resolvedModelId = resolveDocumentCraftingModelId(models.data, modelId, base.model_id);
  const busy = createRevision.isPending;
  const focusPath = focusField ? normalizeErrorPath(focusField) : null;
  const manualBase = base.origin === DocumentCraftingRevisionOrigin.MANUAL_UPLOAD;
  const lowConfidence = base.ingest_confidence === DocumentCraftingIngestConfidence.LOW;

  // `?field=` (a quality note's form field): scroll to it once.
  useEffect(() => {
    if (focusPath) focusFormField(focusPath);
  }, [focusPath]);

  const change = (path: string, value: unknown) => {
    setValues((current) => setAt(current, path, value));
    setErrors((current) => withoutErrors(current, path));
  };
  const changeFiles = (path: string, update: (current: PendingFile[]) => PendingFile[]) => {
    setFiles((current) => ({ ...current, [path]: update(current[path] ?? []) }));
    setErrors((current) => withoutErrors(current, path));
  };

  const selectedIds = selectedUploadIds(values, fileFields);
  const summary = {
    changeRequest,
    fieldsChanged: structuredFieldsChanged(editable, initial.values, values),
    newFiles: countPendingFiles(files),
    untickedUploads: countNewlyUnticked(initial.selectedIds, selectedIds),
  };

  const validate = (): FormErrors => {
    const formErrors = validateFields(editable, {
      root: values,
      files,
      fields,
      uploads: initial.uploadsByPath,
    });
    const ruleErrors = typeRuleErrors(definition.key, values);
    Object.keys(ruleErrors).forEach((path) => {
      if (isKnownPath(path, editable)) formErrors[path] = ruleErrors[path];
    });
    const requestError = validateText(changeRequest, {
      required: true,
      maxLength: CHANGE_REQUEST_MAX_LENGTH,
    });
    if (requestError) formErrors[CHANGE_REQUEST_KEY] = requestError;
    if (!models.isLoading && resolvedModelId === null) formErrors[MODEL_KEY] = { key: "modelRequired" };
    if (countPendingFiles(files) > MAX_FILES_PER_RUN) {
      formErrors[TOTAL_FILES_KEY] = { key: "tooManyFilesTotal", params: { max: MAX_FILES_PER_RUN } };
    }
    return formErrors;
  };

  const showErrors = (formErrors: FormErrors) => {
    setErrors(formErrors);
    const first = Object.keys(formErrors)[0];
    if (first) focusFormField(first);
  };

  const start = async (discardExistingDraft: boolean) => {
    setSubmitError(null);
    if (resolvedModelId === null) return;
    const payload: DocumentCraftingRevisePayload = {
      base_revision_id: base.id,
      change_request: changeRequest.trim(),
      values: trimValues(values),
      model_id: resolvedModelId,
      unticked_upload_ids: untickedUploadIds(base.uploads ?? [], selectedIds, initial.otherUploadIds),
      ...(discardExistingDraft ? { discard_existing_draft: true } : {}),
      ...(Object.keys(altsForRequest(files)).length ? { upload_alts: altsForRequest(files) } : {}),
      ...(Object.keys(altUpdates).length ? { upload_alt_updates: altUpdates } : {}),
    };
    try {
      const result = await createRevision.mutateAsync({
        documentId: document.id,
        payload,
        files: filesForRequest(files),
      });
      navigate(documentCraftingPaths.run(result.run_id));
    } catch (error) {
      // The request already showed a toast (except for draft_exists); put
      // field errors where they belong.
      const body = getDocumentCraftingError(error);
      const filePath =
        typeof body?.field === "string" ? fileFieldPath(normalizeErrorPath(body.field), editable) : null;
      setDialog(null);
      if (body?.code === "draft_exists") {
        setDialog("draftExists");
      } else if (body?.code === "invalid_form") {
        showErrors(mapServerErrors((body as DocumentCraftingInvalidFormError).errors, editable));
      } else if (body?.code === "model_not_accessible") {
        showErrors({
          [MODEL_KEY]: { message: documentCraftingErrorMessage(error, t("create.errors.modelRequired")) },
        });
      } else if (filePath && isKnownPath(filePath, editable)) {
        // A rejected upload (file type, size, damaged file) names its field.
        showErrors({
          [filePath]: { message: documentCraftingErrorMessage(error, t("create.revise.submitFailed")) },
        });
      } else {
        setSubmitError(documentCraftingErrorMessage(error, t("create.revise.submitFailed")));
      }
    }
  };

  const submit = () => {
    const formErrors = validate();
    if (Object.keys(formErrors).length) {
      showErrors(formErrors);
      return;
    }
    if (handEditable && isSmallChange(summary)) {
      setDialog("smallChange");
      return;
    }
    start(false);
  };

  const downloadNative = async () => {
    setDownloading(true);
    try {
      const response = await downloadDocumentCraftingRevisionFile(document.id, base.id, "native");
      downloadDocumentBlob(response, downloadFileName(document.title, base.number, nativeExt));
    } catch {
      // The request already showed what went wrong.
    }
    setDownloading(false);
  };

  const handEditButtons = (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={downloading ? <CircularProgress size={14} /> : <DownloadRounded />}
        onClick={downloadNative}
        disabled={downloading || !base.artefacts?.native}
      >
        {t("create.revise.smallChange.download", { ext: nativeExt.toUpperCase() })}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<UploadFileOutlined />}
        onClick={() => setDialog("upload")}
      >
        {t("create.revise.smallChange.upload")}
      </Button>
    </Box>
  );

  const unknownErrors = Object.keys(errors).filter(
    (path) => !OWN_KEYS.includes(path) && !isKnownPath(path, editable),
  );
  const draftUrl = `${documentCraftingPaths.document(document.id)}?revision=${DRAFT_PARAM}`;

  const schemaFormProps = {
    schema: fields,
    values,
    errors,
    onChange: change,
    files,
    onFilesChange: changeFiles,
    uploads: initial.uploadsByPath,
    uploadsDocumentId: document.id,
    workspaceMaxMb,
    uploadAlts: altUpdates,
    onUploadAltChange: (uploadId: number, alt: string) =>
      setAltUpdates((current) => ({ ...current, [uploadId]: alt })),
    disabled: busy,
    highlightPath: focusPath,
  };

  return (
    <>
      <PageHeader
        title={t("create.revise.title")}
        subtitle={[document.title, labels.type(document.type_key)].filter(Boolean).join(" · ")}
        actions={
          <Button
            color="secondary"
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate(documentCraftingPaths.document(document.id))}
            disabled={busy}
          >
            {t("nav.backToDocument")}
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 340px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          {handEditable && (
            <Alert severity="info">
              <AlertTitle>{t("create.revise.smallChange.title")}</AlertTitle>
              {t("create.revise.smallChange.text", { ext: nativeExt.toUpperCase() })}
              {handEditButtons}
            </Alert>
          )}
          {manualBase && (
            <Alert severity="warning">
              <AlertTitle>{t("create.revise.manualBase.title")}</AlertTitle>
              {t("create.revise.manualBase.text")}
              {lowConfidence && (base.ingest_notes ?? []).length > 0 && (
                <>
                  <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600 }}>
                    {t("create.revise.manualBase.notes")}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {base.ingest_notes.map((note, index) => (
                      <li key={`${index}-${note}`}>{note}</li>
                    ))}
                  </Box>
                </>
              )}
            </Alert>
          )}

          <Panel title={t("create.revise.changePanel")}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t("create.wizard.errorSummary")}
                {unknownErrors.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {unknownErrors.map((path) => (
                      <li key={path}>{`${path}: ${errorText(errors[path])}`}</li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("create.revise.base")}
                value={base.id}
                disabled={busy}
                onChange={(event) => onBaseChange(Number(event.target.value))}
                helperText={t("create.revise.baseHelp")}
              >
                {document.revisions.map((revision) => (
                  <MenuItem key={revision.id} value={revision.id}>
                    {[
                      t("common.revisions.revision", { number: revision.number }),
                      formatDate(revision.saved_at ?? revision.created_at, i18n.language),
                      userName(revision.saved_by ?? revision.created_by),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id={`dc-field-${CHANGE_REQUEST_KEY}`}
                fullWidth
                required
                multiline
                minRows={4}
                maxRows={14}
                size="small"
                label={t("create.revise.changeRequest")}
                placeholder={t("create.revise.changeRequestPlaceholder")}
                value={changeRequest}
                disabled={busy}
                onChange={(event) => {
                  setChangeRequest(event.target.value);
                  setErrors((current) => withoutErrors(current, CHANGE_REQUEST_KEY));
                }}
                inputProps={{ maxLength: CHANGE_REQUEST_MAX_LENGTH }}
                error={!!errors[CHANGE_REQUEST_KEY]}
                helperText={
                  errorText(errors[CHANGE_REQUEST_KEY]) ??
                  t("create.revise.changeRequestHelp", {
                    length: changeRequest.length,
                    max: CHANGE_REQUEST_MAX_LENGTH,
                  })
                }
              />
              <Box id={`dc-field-${MODEL_KEY}`}>
                <ModelSelect
                  value={modelId}
                  onChange={(id) => {
                    setModelId(id);
                    setErrors((current) => withoutErrors(current, MODEL_KEY));
                  }}
                  preferredModelId={base.model_id}
                  disabled={busy}
                  error={!!errors[MODEL_KEY]}
                  helperText={errorText(errors[MODEL_KEY]) ?? t("create.revise.modelHelp")}
                />
              </Box>
            </Box>
          </Panel>

          {FORM_STEPS.map((step) => {
            const stepFields = fieldsOfStep(editable, step);
            if (!stepFields.length) return null;
            return (
              <Panel
                key={step}
                title={t(`create.wizard.steps.${step}`)}
                subtitle={step === "files" ? t("create.revise.filesHint") : undefined}
              >
                <SchemaForm fields={stepFields} {...schemaFormProps} />
                {step === "files" && errors[TOTAL_FILES_KEY] && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {errorText(errors[TOTAL_FILES_KEY])}
                  </Alert>
                )}
              </Panel>
            );
          })}

          <Panel>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: color.gray500 }}>
                {t("create.revise.whatHappensNext")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeRounded />}
                onClick={submit}
                disabled={busy || models.isLoading}
                sx={{ flexShrink: 0 }}
              >
                {busy ? t("create.wizard.submitting") : t("create.revise.submit")}
              </Button>
            </Box>
          </Panel>
        </Box>

        <HistoryPanel
          document={document}
          base={base}
          readOnlyFields={readOnlyFields}
          values={values}
        />
      </Box>

      <ConfirmDialog
        open={dialog === "smallChange"}
        title={t("create.revise.smallChange.confirmTitle")}
        text={t("create.revise.smallChange.confirmText", {
          ext: nativeExt.toUpperCase(),
          limit: SMALL_CHANGE_REQUEST_LIMIT,
        })}
        confirmLabel={t("create.revise.smallChange.continue")}
        pending={busy}
        onConfirm={() => start(false)}
        onClose={() => setDialog(null)}
      >
        {handEditButtons}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "draftExists"}
        title={t("create.revise.draftExists.title")}
        text={t("create.revise.draftExists.text")}
        confirmLabel={t("create.revise.draftExists.continue")}
        pending={busy}
        secondaryAction={{
          label: t("create.revise.draftExists.discard"),
          onClick: () => start(true),
        }}
        onConfirm={() => navigate(draftUrl)}
        onClose={() => setDialog(null)}
      />

      {handEditable && (
        <UploadRevisionDialog
          open={dialog === "upload"}
          documentId={document.id}
          nativeExt={nativeExt}
          maxUploadMb={workspaceMaxMb}
          revisions={document.revisions}
          defaultBaseId={base.id}
          hasDraft={!!document.my_draft_revision}
          onOpenDraft={() => navigate(draftUrl)}
          onClose={() => setDialog(null)}
          onStarted={(runId) => navigate(documentCraftingPaths.run(runId))}
        />
      )}
    </>
  );
};

/** Waits for the base revision's inputs, then mounts the form once. */
const ReviseLoader: React.FC<{
  document: DocumentCraftingDocumentDetail;
  definition: DocumentCraftingTypeDefinition;
  baseId: number;
  focusField: string | null;
  onBaseChange: (revisionId: number) => void;
}> = ({ document, definition, baseId, focusField, onBaseChange }) => {
  const revision = useDocumentCraftingRevision(document.id, baseId);
  if (revision.isLoading) return <LoadingState />;
  if (revision.isError || !revision.data) {
    return <ErrorState onRetry={() => revision.refetch()} />;
  }
  return (
    <ReviseForm
      key={`${document.id}-${baseId}`}
      document={document}
      definition={definition}
      base={revision.data}
      initial={initialFromBase(definition.fields, revision.data)}
      focusField={focusField}
      // The document's limit, not the viewer's workspace setting: a personal
      // document keeps the module limit (`max_upload_mb` of the detail).
      workspaceMaxMb={document.max_upload_mb ?? null}
      onBaseChange={onBaseChange}
    />
  );
};

const Blocked: React.FC<{ documentId: number; title: string; text: string }> = ({
  documentId,
  title,
  text,
}) => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title={t("create.revise.title")}
        subtitle={title}
        actions={
          <Button
            color="secondary"
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate(documentCraftingPaths.document(documentId))}
          >
            {t("nav.backToDocument")}
          </Button>
        }
      />
      <Alert severity="warning">{text}</Alert>
    </>
  );
};

/**
 * `/document-crafting/:documentId/revise?base=<revisionId>&field=<path>`:
 * a new AI revision. The form is the create form pre-filled from the base
 * revision (default: the latest saved one) with the briefing and earlier
 * change requests as read-only history, the earlier uploads (can be
 * unticked), new uploads, a required change request and the model (default:
 * the base revision's). `field` focuses one form field.
 */
const ReviseDocument: React.FC = () => {
  const { t, i18n } = useTranslation("documentCrafting");
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const documentId = Number(params.documentId) || undefined;
  const documentQuery = useDocumentCraftingDocument(documentId);
  const types = useDocumentCraftingTypes();

  if (documentQuery.isLoading || types.isLoading) return <LoadingState />;
  if (documentQuery.isError || !documentQuery.data || !documentId) {
    return (
      <>
        <PageHeader title={t("create.revise.title")} />
        <ErrorState onRetry={() => documentQuery.refetch()} />
      </>
    );
  }
  const document = documentQuery.data;
  if (types.isError || !types.data) return <ErrorState onRetry={() => types.refetch()} />;

  if (!document.permissions.can_edit) {
    return <Blocked documentId={document.id} title={document.title} text={t("create.revise.noPermission")} />;
  }
  const definition = types.data.types.find((item) => item.id === document.type);
  if (!definition) {
    return <Blocked documentId={document.id} title={document.title} text={t("create.picker.unknownType")} />;
  }
  if (!document.revisions.length) {
    return <Blocked documentId={document.id} title={document.title} text={t("create.revise.noRevision")} />;
  }

  const requested = Number(searchParams.get("base"));
  const base =
    document.revisions.find((revision) => revision.id === requested) ?? document.revisions[0];
  const missingBase = !!searchParams.get("base") && base.id !== requested;

  return (
    <>
      {missingBase && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("create.revise.baseMissing", {
            number: base.number,
            date: formatDateTime(base.saved_at, i18n.language),
          })}
        </Alert>
      )}
      <ReviseLoader
        key={base.id}
        document={document}
        definition={definition}
        baseId={base.id}
        focusField={searchParams.get("field")}
        onBaseChange={(revisionId) => setSearchParams({ base: String(revisionId) })}
      />
    </>
  );
};

export default ReviseDocument;
